import { useMemo } from 'react'
import { type MRT_ColumnDef } from 'material-react-table'
import { Add, DeleteOutline, HelpOutline } from '@mui/icons-material'
import { Button, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { QueryKey, useQueryClient } from '@tanstack/react-query'
import useOverlay, { OverlayState } from '@/hooks/useOverlay'
import {
  useCreateVendorAlias,
  useDeleteVendorAlias,
} from '@/services/hooks/relay/dynamic'
import ValidationTableThemeProvider from '@/theme/ValidationTableThemeProvider'
import { LiveListEntity } from '@/types/live-lists'
import { BaseVendor } from '@/types/relay'
import { VendorAlias, VendorSettings } from '@/types/vendor-settings'
import { showSuccessSnackbar, showErrorSnackbar } from '@/utils/snackbars'
import useZerapixTable from '@/components/zerapix-table/useZerapixTable'
import ZerapixTable from '@/components/zerapix-table/ZerapixTable'
import AddVendorAliasDialog, { FormValues } from './AddVendorAliasDialog'
import { useCredentialTypeContext } from './CredentialTypeProvider'

type VendorAliasesTableContentProps = {
  aliases: VendorAlias[]
  canEdit: boolean
  vendor?: LiveListEntity<BaseVendor> | null
  isLoading?: boolean
  queryKey?: QueryKey
  addAliasOverlay: OverlayState
  deleteEnabled?: boolean
  inValidationOverlay?: boolean
}

type VendorAliasesTableProps = Omit<
  VendorAliasesTableContentProps,
  'addAliasOverlay'
> & {
  disabledSaveHelperText?: string
  orgId: string
  addDefaultValues?: FormValues
}

function VendorAliasesTableContent({
  aliases,
  vendor,
  canEdit,
  isLoading,
  queryKey,
  addAliasOverlay,
  deleteEnabled = true,
  inValidationOverlay = false,
}: VendorAliasesTableContentProps) {
  const queryClient = useQueryClient()
  const credentialType = useCredentialTypeContext()

  const { deleteVendorAlias } = useDeleteVendorAlias({
    orgCredentialTypeId: credentialType.id,
    sideEffectQueryKeys: queryKey ? [queryKey] : undefined,
    onSuccess: () => {
      showSuccessSnackbar('Alias Deleted')
    },
    onError: () => {
      showErrorSnackbar('Failed to delete alias. Please try again later.')
    },
    onMutate: async (deletedAlias) => {
      if (!queryKey) return
      await queryClient.cancelQueries({ queryKey })

      // Optimistically update the table
      queryClient.setQueryData(queryKey, (old: VendorSettings) => {
        if (!old) return old
        return {
          ...old,
          aliases: old.aliases.filter(
            (alias) =>
              !(
                alias.vendor_name === deletedAlias.vendor_name &&
                alias.remit_address === deletedAlias.remit_address &&
                alias.vendor_address === deletedAlias.vendor_address
              ),
          ),
        }
      })
    },
  })

  const columns = useMemo<MRT_ColumnDef<VendorAlias>[]>(
    () => [
      {
        accessorKey: 'vendor_name',
        header: 'Vendor Name',
      },
      {
        accessorKey: 'remit_address',
        header: 'Remit Address',
      },
      {
        accessorKey: 'vendor_address',
        header: 'Vendor Address',
      },
    ],
    [],
  )

  const table = useZerapixTable({
    data: aliases,
    columns,
    state: { isLoading: !!isLoading },
    enableColumnActions: false,
    enableSorting: false,
    enableFilters: false,
    enableDensityToggle: false,
    enableHiding: false,
    enablePagination: false,
    enableBottomToolbar: false,
    enableRowActions: deleteEnabled,
    positionActionsColumn: 'last',
    muiTableBodyCellProps: {
      sx: {
        overflow: 'hidden',
        whiteSpace: 'normal',
      },
    },
    muiTablePaperProps: {
      variant: 'outlined',
      elevation: 0,
    },
    renderTopToolbar: () => (
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          px: inValidationOverlay ? 1 : 2,
          py: inValidationOverlay ? 0.5 : 1,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant={inValidationOverlay ? 'body2' : 'subtitle1'}>
            Vendor Aliases
          </Typography>
          <Tooltip
            title={`Vendor aliases are used to match data on an invoice or receipt to a vendor in ${credentialType.name}. Aliases can also be added when viewing a document.`}
          >
            <HelpOutline sx={{ color: 'text.secondary' }} fontSize="small" />
          </Tooltip>
        </Stack>
        {canEdit && (
          <Button
            size={inValidationOverlay ? 'small' : undefined}
            variant="text"
            startIcon={<Add />}
            onClick={addAliasOverlay.open}
            disabled={!vendor}
          >
            Add Alias
          </Button>
        )}
      </Stack>
    ),
    renderRowActions: deleteEnabled
      ? ({ row }) => (
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => deleteVendorAlias(row.original)}
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      : undefined,
  })

  return <ZerapixTable<VendorAlias> table={table} />
}

export default function VendorAliasesTable({
  addDefaultValues,
  orgId,
  disabledSaveHelperText,
  ...props
}: VendorAliasesTableProps) {
  const { inValidationOverlay, vendor, queryKey } = props
  const addAliasOverlay = useOverlay()
  const queryClient = useQueryClient()
  const credentialType = useCredentialTypeContext()

  const { createVendorAlias } = useCreateVendorAlias({
    orgCredentialTypeId: credentialType.id,
    sideEffectQueryKeys: queryKey ? [queryKey] : undefined,
    onSuccess: () => {
      showSuccessSnackbar('Alias Added')
    },
    onError: () => {
      showErrorSnackbar('Failed to add alias. Please try again later.')
    },
    onMutate: async (newAliasDetails) => {
      if (!queryKey) return
      await queryClient.cancelQueries({ queryKey })

      // Optimistically update the table
      queryClient.setQueryData(queryKey, (old: VendorSettings) => {
        if (!old) return old
        return {
          ...old,
          aliases: [...(old.aliases || []), newAliasDetails],
        }
      })
    },
  })

  return (
    <>
      {inValidationOverlay && (
        <ValidationTableThemeProvider>
          <VendorAliasesTableContent
            {...props}
            addAliasOverlay={addAliasOverlay}
          />
        </ValidationTableThemeProvider>
      )}
      {!inValidationOverlay && (
        <VendorAliasesTableContent
          {...props}
          addAliasOverlay={addAliasOverlay}
        />
      )}
      {vendor && props.canEdit && (
        <AddVendorAliasDialog
          overlay={addAliasOverlay}
          defaultValues={addDefaultValues}
          disabledSaveHelperText={disabledSaveHelperText}
          vendor={vendor}
          orgId={orgId}
          onAdd={(newAlias) => {
            addAliasOverlay.close()
            createVendorAlias(newAlias)
          }}
        />
      )}
    </>
  )
}
